// Cálculo do split de pagamento entre Vigor e empresa parceira (Gran).
//
// QUEM EMITE (26/09/2026): a VIGOR. O boleto nasce na conta Asaas da Vigor, o
// dinheiro entra lá, a Vigor paga a Equatorial e a parte da parceira sai por
// split na hora do pagamento. Entre 06/08 e 26/09/2026 era o contrário (a
// parceira emitia e repassava a Vigor); os boletos emitidos nesse período
// continuam na conta da parceira até serem pagos. Quanto vai no split é
// `calcularRepasseParceira`, no fim deste arquivo.
//
// REGRA (definida em 03/08/2026 — substitui o percentual fixo anterior):
//
//   A Vigor vende energia para a parceira com deságio sobre a tarifa CHEIA da
//   distribuidora. Com deságio de 35%, a Vigor recebe 65% da tarifa cheia —
//   sempre, e sempre PRIMEIRO. A parceira decide, UC a UC, quanto de desconto
//   repassa ao consumidor final, e fica com o que sobrar entre o que o
//   consumidor paga e o que a Vigor tem a receber.
//
//   Desde 26/09/2026 o percentual da Vigor é POR UC
//   (`unidades_consumidoras.percentual_vigor`), definido pelo Financeiro ou adm.
//   Antes era um só para a parceira inteira (`empresas_parceiras.percentual_vigor`).
//
//   Exemplo (1.000 kWh, tarifa cheia 1,185396, desconto ao consumidor 25%):
//     consumidor paga  1000 × 1,185396 × 0,75 = R$ 889,05
//     Vigor recebe     1000 × 1,185396 × 0,65 = R$ 770,51
//     parceira fica com o residual            = R$ 118,54
//
//   O desconto ao consumidor nunca pode passar do deságio (35%): no teto exato
//   o residual da parceira é zero, e acima dele ficaria negativo. Isso é barrado
//   no cadastro da UC (trigger valida_desconto_ate_desagio) e de novo aqui, para
//   nenhum boleto sair com split errado.
//
// O QUE ENTRA NO SPLIT: energia compensada (SCEE) e bandeira tarifária, ambas
// pela mesma regra. Tudo o mais fica 100% com a Vigor:
//   - juros, multa e parcelamento do boleto da Vigor — é ela que carrega o atraso;
//   - restituição de imposto, energia não isenta e parcela injetada sem desconto.
//
// A parte da Equatorial embutida no boleto único (`valor_fatura_distribuidora`)
// também fica com a Vigor, que é quem paga a distribuidora desde 26/09/2026.
//
// FORA DA PARCERIA, desde 22/09/2026: cliente PRÓPRIO da parceira, sem nada a ver
// com a Vigor (`faturas.fora_parceria_vigor`). Nenhuma das regras acima vale — o
// boleto continua saindo na conta da parceira. Ver `calcularCobrancaForaParceria`.

import { validaLinhaDigitavel, valorDaLinhaDigitavel } from "./fatura-parser/boleto-pix.ts";

/** Campos de `faturas` usados no cálculo. */
export interface FaturaSplitInput {
    qtd_injecao_scee_boleto?: number | string | null;
    tarifa_injecao_gd1?: number | string | null;
    valor_tarifa_distribuidora?: number | string | null;
    desconto_tarifa?: number | string | null;
    qtd_adc_bandeira_boleto?: number | string | null;
    tarifa_bandeira?: number | string | null;
    desconto_bandeira?: number | string | null;
    /**
     * Parte do boleto que não é da Equatorial. Opcional: sem ele a trava de
     * `parte_parceira_acima_do_boleto` não roda.
     */
    valor_boleto_vigor?: number | string | null;
}

export type SplitParceiraStatus =
    /** Há residual para a parceira — emite com split. */
    | "ok"
    /** Residual é zero (desconto ao consumidor igual ao deságio) — emite sem split. */
    | "sem_repasse"
    /** Falta a tarifa cheia para saber quanto é da Vigor — NÃO emitir. */
    | "dados_insuficientes"
    /** Desconto ao consumidor maior que o deságio — NÃO emitir. */
    | "desconto_acima_do_desagio"
    /** A parte da parceira não cabe no valor do boleto sem a Equatorial — NÃO emitir. */
    | "parte_parceira_acima_do_boleto";

export interface SplitParceiraResult {
    status: SplitParceiraStatus;
    /** Valor em reais a repassar para a wallet da parceira. Nunca negativo. */
    valorParceira: number;
    /** O que a Vigor tem a receber de energia + bandeira (antes de juros, Equatorial etc.). */
    valorVigor: number;
    /** O que o consumidor está pagando de energia + bandeira. */
    baseCliente: number;
    /** Mensagem pronta para o operador quando o status impede a emissão. */
    motivo?: string;
    detalhe: {
        energiaCliente: number;
        energiaVigor: number;
        bandeiraCliente: number;
        bandeiraVigor: number;
    };
}

const num = (v: number | string | null | undefined): number => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
};

/** Arredonda para centavos. */
const round2 = (v: number): number => Math.round(v * 100) / 100;

/** Compara em centavos, para tolerar ruído de ponto flutuante. */
const CENTAVO = 0.005;

/**
 * Calcula quanto vai para a empresa parceira num boleto.
 *
 * @param fatura Linha de `faturas` (os campos já gravados pelo modal de emissão).
 * @param percentualVigor `unidades_consumidoras.percentual_vigor` da UC da fatura —
 *   a fatia da tarifa cheia que é da Vigor (65 quando o deságio é 35%).
 */
export function calcularSplitParceira(
    fatura: FaturaSplitInput,
    percentualVigor: number | string | null | undefined,
): SplitParceiraResult {
    const fatorVigor = num(percentualVigor) / 100;

    const kwhEnergia = num(fatura.qtd_injecao_scee_boleto);
    const tarifaCheia = num(fatura.valor_tarifa_distribuidora);
    const kwhBandeira = num(fatura.qtd_adc_bandeira_boleto);
    const bandeiraCheia = num(fatura.tarifa_bandeira);

    // Tarifa efetivamente cobrada do consumidor. `tarifa_injecao_gd1` é editável no
    // modal de emissão e é o que de fato foi para o boleto; só cai no cálculo a
    // partir do desconto quando a fatura é antiga e não tem o campo gravado.
    const tarifaCliente = fatura.tarifa_injecao_gd1 != null
        ? num(fatura.tarifa_injecao_gd1)
        : tarifaCheia * (1 - num(fatura.desconto_tarifa));

    const energiaCliente = kwhEnergia * tarifaCliente;
    const energiaVigor = kwhEnergia * tarifaCheia * fatorVigor;

    const bandeiraCliente = kwhBandeira * bandeiraCheia * (1 - num(fatura.desconto_bandeira));
    const bandeiraVigor = kwhBandeira * bandeiraCheia * fatorVigor;

    const baseCliente = round2(energiaCliente + bandeiraCliente);
    const valorVigor = round2(energiaVigor + bandeiraVigor);
    const residual = round2(baseCliente - valorVigor);

    const detalhe = {
        energiaCliente: round2(energiaCliente),
        energiaVigor: round2(energiaVigor),
        bandeiraCliente: round2(bandeiraCliente),
        bandeiraVigor: round2(bandeiraVigor),
    };

    const base = { valorParceira: 0, valorVigor, baseCliente, detalhe };

    // Sem percentual configurado não dá para saber a fatia da Vigor, e a UC nova
    // não herda nenhum: cada UC tem o seu, decidido pelo Financeiro. Bloqueia.
    if (fatorVigor <= 0) {
        return {
            ...base,
            status: "dados_insuficientes",
            motivo: "Esta UC está sem o % da Vigor. O Financeiro ou um adm define no cadastro da UC "
                + "(Financeiro → UCs → editar) antes de emitir.",
        };
    }

    // Está cobrando energia mas não temos a tarifa cheia gravada: sem ela o alvo da
    // Vigor sai zero e a parceira levaria o boleto inteiro. Bloqueia.
    if (baseCliente > CENTAVO && tarifaCheia <= 0 && kwhEnergia > 0) {
        return {
            ...base,
            status: "dados_insuficientes",
            motivo: "Fatura sem a tarifa cheia da distribuidora (valor_tarifa_distribuidora) — "
                + "não é possível calcular a parte da Vigor.",
        };
    }

    if (residual < -CENTAVO) {
        const descontoMax = round2((1 - fatorVigor) * 100);
        return {
            ...base,
            status: "desconto_acima_do_desagio",
            motivo: `O desconto concedido ao consumidor supera o deságio de ${descontoMax}% desta UC: `
                + `o boleto cobra R$ ${baseCliente.toFixed(2)} de energia/bandeira, mas a Vigor tem a receber `
                + `R$ ${valorVigor.toFixed(2)}. Corrija o desconto ou o % da Vigor da UC antes de emitir.`,
        };
    }

    if (residual <= CENTAVO) {
        return { ...base, status: "sem_repasse" };
    }

    // O residual sai das linhas de energia, mas o boleto cobra `valor_boleto_vigor`,
    // que o operador pode editar à mão. Zerar só o valor (e não a energia) deixava o
    // boleto só com a Equatorial e o split ainda mandava a parte da parceira — paga
    // com o dinheiro da conta de luz, que a Vigor precisa para pagar a Equatorial.
    if (fatura.valor_boleto_vigor != null && residual > num(fatura.valor_boleto_vigor) + CENTAVO) {
        return {
            ...base,
            status: "parte_parceira_acima_do_boleto",
            motivo: `A parte da parceira (R$ ${residual.toFixed(2)}) é maior que a parte do boleto sem a Equatorial `
                + `(R$ ${num(fatura.valor_boleto_vigor).toFixed(2)}): o split sairia do dinheiro da conta de luz. `
                + "Se não houve injeção, zere a quantidade de energia e de bandeira nos detalhes da cobrança, "
                + "em vez de zerar o valor do boleto.",
        };
    }

    return { ...base, status: "ok", valorParceira: residual };
}

export interface RepasseParceiraResult {
    /**
     * `ok` = emite com o split para a parceira; `sem_split` = emite SEM split,
     * porque não sobra nada para ela (residual zero, ou a taxa comeu o residual);
     * `invalido` = não emitir (o motivo explica).
     */
    status: "ok" | "sem_split" | "invalido";
    /** `fixedValue` do split, em reais, para a carteira da parceira. */
    valorSplitParceira: number;
    /** Taxa do Asaas (valor − líquido simulado) descontada da parte da parceira. */
    taxa: number;
    /**
     * `true` quando a taxa é maior que o residual: a parceira fica com zero e o
     * pedaço da taxa que passa do residual sai da Vigor. Acontece nas UCs com
     * desconto ao consumidor igual ao deságio, onde o residual é zero ou quase.
     */
    taxaMaiorQueResidual: boolean;
    motivo?: string;
}

/**
 * Quanto sai no split para a carteira da PARCEIRA, agora que é a VIGOR quem
 * emite o boleto (26/09/2026).
 *
 * Tudo cai na conta Asaas da Vigor: a fatia dela na tarifa cheia, a parte da
 * Equatorial (a Vigor paga a distribuidora) e juros/multa/parcelamento. Só o
 * residual da parceira sai, num único split:
 *
 *     split = residual − taxa do Asaas
 *
 * **A TAXA SAI DA PARTE DA PARCEIRA** (decisão do usuário em 26/09/2026). A taxa
 * é a SIMULADA na emissão (`POST /v3/payments/simulate`), porque o split é valor
 * fixo e a taxa real só se conhece no pagamento; a diferença entre as duas sobra
 * ou falta na conta de quem emite — a Vigor.
 *
 * Quando a taxa passa do residual a parceira fica com zero e o boleto sai sem
 * split: split negativo não existe. O teto do Asaas (soma dos splits ≤ líquido)
 * nunca morde aqui, porque `residual − taxa ≤ valor − taxa = líquido`.
 *
 * @param valorTotal Valor da cobrança (o que o consumidor paga).
 * @param valorLiquido `netValue` do simulate. Só é exigido quando há residual.
 * @param valorParceira `valorParceira` de `calcularSplitParceira`.
 */
export function calcularRepasseParceira(params: {
    valorTotal: number | string | null | undefined;
    valorLiquido: number | string | null | undefined;
    valorParceira: number | string | null | undefined;
}): RepasseParceiraResult {
    const total = num(params.valorTotal);
    const residual = Math.max(0, num(params.valorParceira));
    const invalido = (motivo: string): RepasseParceiraResult =>
        ({ status: "invalido", valorSplitParceira: 0, taxa: 0, taxaMaiorQueResidual: false, motivo });

    // Sem residual não há o que repassar, e o boleto fica inteiro na Vigor. É o
    // caso da UC com desconto igual ao deságio e da UC sem injeção no ciclo (o
    // boleto é só a conta da Equatorial, que a Vigor vai pagar).
    if (residual <= CENTAVO) {
        return { status: "sem_split", valorSplitParceira: 0, taxa: 0, taxaMaiorQueResidual: false };
    }

    if (residual > total + CENTAVO) {
        return invalido(
            `A parte da parceira (R$ ${residual.toFixed(2)}) é maior que o boleto (R$ ${total.toFixed(2)}). `
            + "Confira os valores da fatura antes de emitir.",
        );
    }

    const liquido = num(params.valorLiquido);
    if (liquido <= 0) {
        return invalido("Não foi possível obter o valor líquido da cobrança no Asaas para descontar a taxa da parte da parceira.");
    }

    const taxa = round2(Math.max(0, total - liquido));
    const repasse = round2(residual - taxa);
    if (repasse < 0.01) {
        return { status: "sem_split", valorSplitParceira: 0, taxa, taxaMaiorQueResidual: true };
    }

    return {
        status: "ok",
        valorSplitParceira: round2(Math.min(repasse, liquido)),
        taxa,
        taxaMaiorQueResidual: false,
    };
}

export interface CobrancaForaParceriaResult {
    /** `ok` = emite SEM split; `invalido` = não emitir (o motivo explica). */
    status: "ok" | "invalido";
    /** O que fica com a parceira depois de pagar a Equatorial, antes da taxa do Asaas. */
    valorParceira: number;
    motivo?: string;
}

/**
 * Cobrança de cliente PRÓPRIO da parceira, fora da parceria com a Vigor
 * (`faturas.fora_parceria_vigor`, pedido do Financeiro em 22/09/2026 — cliente da
 * usina do dono da Gran).
 *
 * O boleto cobra a conta da Equatorial + a parte da parceira e sai na conta Asaas
 * dela, **sem split**: a Vigor não vendeu energia nenhuma e não recebe nada. Quando
 * o cliente paga, `fn_enfileirar_pagamento_equatorial` paga a Equatorial pela conta
 * da parceira e o resto fica com ela. A taxa do Asaas sai da parte da parceira, como
 * em qualquer boleto que ela emite.
 *
 * Por que não passar por `calcularRepasseParceira`: aquele é o boleto da parceria,
 * que nasce na conta da Vigor e divide a energia pelo % da UC. Aqui a Vigor não
 * tem parte nenhuma e o boleto nem passa pela conta dela.
 *
 * Trava o que tornaria o pagamento da Equatorial impossível ou errado:
 *  - sem a linha digitável válida o sistema não tem com o que pagar a conta;
 *  - quando a linha carrega valor, ele tem que bater com o informado — a parceira
 *    retém o valor informado e o Asaas paga o do código de barras (um centavo de
 *    diferença já derrubou repasse, ver processar-fila-pagamento-equatorial);
 *  - a parte da Equatorial não pode passar do boleto.
 */
export function calcularCobrancaForaParceria(params: {
    valorTotal: number | string | null | undefined;
    parteEquatorial: number | string | null | undefined;
    linhaDigitavelEquatorial: string | null | undefined;
    /** `faturas.debito_automatico_equatorial`, lido da própria conta pelo parser. */
    debitoAutomatico?: boolean | null;
}): CobrancaForaParceriaResult {
    const total = round2(num(params.valorTotal));
    const equatorial = round2(num(params.parteEquatorial));
    const linha = String(params.linhaDigitavelEquatorial ?? "").replace(/\D/g, "");
    const invalido = (motivo: string): CobrancaForaParceriaResult =>
        ({ status: "invalido", valorParceira: 0, motivo });

    if (!(total > 0)) return invalido("O valor do boleto é R$ 0,00 — confira os valores antes de emitir.");
    // Débito automático: a própria Equatorial debita do cliente. Embutir a conta no
    // boleto faria o sistema pagá-la de novo pela conta da parceira.
    if (params.debitoAutomatico === true) {
        return invalido(
            "A conta da Equatorial está em débito automático: a Equatorial vai debitar do cliente sozinha. "
            + "Não dá para embutir no boleto — peça o cancelamento do débito automático antes.",
        );
    }
    if (!(equatorial > 0)) {
        return invalido("Informe o valor da conta da Equatorial: é ela que o sistema paga quando o cliente pagar o boleto.");
    }
    if (!linha) {
        return invalido("Informe a linha digitável da conta da Equatorial: sem ela o sistema não consegue pagar a conta.");
    }
    if (!validaLinhaDigitavel(linha)) {
        return invalido("A linha digitável da Equatorial não confere (dígito verificador errado). Copie de novo da conta.");
    }
    const valorDaLinha = valorDaLinhaDigitavel(linha);
    if (valorDaLinha != null && Math.abs(valorDaLinha - equatorial) > CENTAVO) {
        return invalido(
            `O valor da Equatorial informado (R$ ${equatorial.toFixed(2)}) não bate com o da linha digitável `
            + `(R$ ${valorDaLinha.toFixed(2)}). O sistema paga o valor da linha: corrija antes de emitir.`,
        );
    }
    if (equatorial > total + CENTAVO) {
        return invalido(
            `A conta da Equatorial (R$ ${equatorial.toFixed(2)}) é maior que o boleto (R$ ${total.toFixed(2)}).`,
        );
    }

    return { status: "ok", valorParceira: round2(Math.max(0, total - equatorial)) };
}

export interface ParteParceiraInjecao {
    /** Energia injetada × tarifa cheia (energia + bandeira): o que o cliente pagaria sem desconto. */
    bruto: number;
    /** Desconto concedido ao cliente sobre o bruto. */
    desconto: number;
    /** O que a parceira cobra: bruto − desconto. */
    parteParceira: number;
}

/**
 * Parte da parceira no boleto avulso de cliente próprio dela, pela energia injetada —
 * a mesma conta que `gerar-boleto-gran/desenhaPaginaGran.ts` imprime no
 * detalhamento ("Energia injetada" − "Desconto do cliente"). Calcular igual aqui é
 * o que faz o Total do PDF bater com as linhas acima dele sem sobrar "Outros
 * lançamentos".
 *
 * Tarifa cheia = energia não compensada + bandeira, ambas com tributos, como a
 * Equatorial cobraria o kWh se não houvesse compensação. O desconto vale igual para
 * as duas (o avulso tem um campo só).
 */
export function calcularParteParceiraPorInjecao(params: {
    injetadaKwh: number | string | null | undefined;
    tarifaDistribuidora: number | string | null | undefined;
    tarifaBandeira: number | string | null | undefined;
    /** Em pontos percentuais: 20 = 20%. */
    descontoPercentual: number | string | null | undefined;
}): ParteParceiraInjecao {
    const kwh = Math.max(0, num(params.injetadaKwh));
    const tarifaCheia = Math.max(0, num(params.tarifaDistribuidora)) + Math.max(0, num(params.tarifaBandeira));
    const fator = Math.min(1, Math.max(0, num(params.descontoPercentual) / 100));
    const bruto = kwh * tarifaCheia;
    const desconto = bruto * fator;
    return { bruto: round2(bruto), desconto: round2(desconto), parteParceira: round2(bruto - desconto) };
}
